import React, { useMemo, useState } from 'react';
import { TRACKERS, TRACKER_KEYS } from './config/trackers';
import { BELONGS_KEYS, BELONGS_META } from './config/belongs';
import {
    deriveTrackerCard,
    deriveBelongsCard,
    buildAlertSummary,
} from './utils/derive';

/**
 * Dashboard — alert strip, 10 tracker cards, belongs quick view, filter card.
 * Class names mirror the v4 mockup. Status color channel is alert/warn/ok.
 */
function Dashboard({ accounts, loaded, onOpenTracker, onOpenBelongs }) {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const trackerCards = useMemo(
        () => TRACKER_KEYS.map((key) => deriveTrackerCard(key, accounts)),
        [accounts],
    );
    const belongsCards = useMemo(
        () => BELONGS_KEYS.reduce((acc, key) => {
            acc[key] = deriveBelongsCard(key, accounts);
            return acc;
        }, {}),
        [accounts],
    );
    const alert = useMemo(() => buildAlertSummary(accounts), [accounts]);

    const visibleCards = useMemo(() => {
        const term = search.trim().toLowerCase();
        return trackerCards.filter((c) => {
            if (filter !== 'all' && c.trackerKey !== filter) return false;
            if (statusFilter !== 'all' && c.cardClass !== statusFilter) return false;
            if (!term) return true;
            return c.tracker.label.toLowerCase().includes(term);
        });
    }, [trackerCards, search, filter, statusFilter]);

    return (
        <>
            {/* Alert strip — warm gradient with attention summary, or green "all clear". */}
            {alert.totalIssues > 0 ? (
                <div className="bt-alert-strip" role="status">
                    <span className="bt-alert-strip-icon" role="img" aria-label="Warning">⚠️</span>
                    <div className="bt-alert-strip-body">
                        <strong>
                            {alert.totalIssues} item{alert.totalIssues === 1 ? '' : 's'} need attention:
                        </strong>{' '}
                        {alert.items.slice(0, 4).join(' · ')}
                        {alert.items.length > 4 ? ` · +${alert.items.length - 4} more` : ''}
                    </div>
                    <button type="button" className="bt-alert-strip-link">View all →</button>
                </div>
            ) : (
                <div className="bt-alert-strip ok" role="status">
                    <span className="bt-alert-strip-icon" role="img" aria-label="All clear">✅</span>
                    <div className="bt-alert-strip-body">
                        <strong>All clear.</strong> Nothing needs attention right now — every tracker is on schedule.
                    </div>
                </div>
            )}

            {/* Section head */}
            <div className="bt-sec-head">
                <div className="bt-sec-title">
                    <span className="bt-sec-title-icon" role="img" aria-label="Trackers">⚡</span>
                    Business Trackers
                </div>
            </div>

            {/* Tracker cards grid */}
            <div className="bt-cards">
                {visibleCards.map((card) => (
                    <div
                        key={card.trackerKey}
                        className={`bt-card ${card.cardClass}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onOpenTracker(card.trackerKey)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onOpenTracker(card.trackerKey);
                        }}
                    >
                        <div className="bt-card-top">
                            <span className="bt-card-icon" role="img" aria-label={card.tracker.label}>
                                {card.tracker.icon}
                            </span>
                            <span className={`bt-badge ${card.badge.cls}`}>
                                {card.badge.text}
                            </span>
                        </div>
                        <div className="bt-card-label">{card.tracker.label}</div>
                        <div className="bt-card-num">{card.count}</div>
                        <div className="bt-card-sub">{card.accountsLabel}</div>
                        <div className={`bt-card-status ${card.status.cls}`}>
                            {card.status.text}
                        </div>
                    </div>
                ))}
                {visibleCards.length === 0 && (
                    <div className="bt-empty" style={{ gridColumn: '1 / -1' }}>
                        No trackers match your filter.
                    </div>
                )}
            </div>

            {/* Belongs Quick View — vertical list with chevron */}
            <div className="bt-belongs-shortcuts">
                <div className="bt-belongs-shortcuts-label">Quick View by Category</div>
                <div className="bt-belongs-shortcuts-row">
                    {BELONGS_KEYS.map((key) => {
                        const meta = BELONGS_META[key];
                        const stat = belongsCards[key] || {};
                        const count = stat.count || 0;
                        let metaNode;
                        if (stat.overdue > 0) {
                            metaNode = (
                                <>
                                    {count} account{count === 1 ? '' : 's'}
                                    {' · '}
                                    <span className="urgent">
                                        <span role="img" aria-label="Urgent">⚠</span> {stat.overdue} urgent
                                    </span>
                                </>
                            );
                        } else if (stat.pending > 0) {
                            metaNode = (
                                <>
                                    {count} account{count === 1 ? '' : 's'}
                                    {' · '}
                                    <span className="warn">{stat.pending} warning{stat.pending === 1 ? '' : 's'}</span>
                                </>
                            );
                        } else {
                            metaNode = `${count} account${count === 1 ? '' : 's'} · on track`;
                        }
                        return (
                            <button
                                key={key}
                                type="button"
                                className="bt-belongs-shortcut"
                                onClick={() => onOpenBelongs(key)}
                            >
                                <span className="bt-belongs-shortcut-icon" role="img" aria-label={meta.label}>
                                    {meta.icon}
                                </span>
                                <div className="bt-belongs-shortcut-body">
                                    <div className="bt-belongs-shortcut-name">{meta.label}</div>
                                    <div className="bt-belongs-shortcut-meta">{metaNode}</div>
                                </div>
                                <span className="bt-belongs-shortcut-arrow" aria-hidden="true">›</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Search + filter card */}
            <div className="bt-filter-card">
                <div className="bt-search-input">
                    <span role="img" aria-label="Search">🔍</span>
                    <input
                        type="text"
                        placeholder="Search tracker name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className="bt-dropdown"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                >
                    <option value="all">All Trackers</option>
                    {TRACKER_KEYS.map((k) => (
                        <option key={k} value={k}>{TRACKERS[k].label}</option>
                    ))}
                </select>
                <select
                    className="bt-dropdown"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">All Status</option>
                    <option value="alert">Has urgent items</option>
                    <option value="warn">Has warnings</option>
                    <option value="ok">All good</option>
                </select>
            </div>

            {!loaded && accounts.length === 0 && (
                <div className="bt-empty">Loading accounts…</div>
            )}
        </>
    );
}

export default Dashboard;
